import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ActiveRemediationRunSummary } from '@libs/types';
import { Box, Button, LinearProgress, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { useActiveRemediationRuns } from '@features/remediation/api/remediation-api';
import { RunStatusBadge } from '@features/remediation/components/RemediationStatusBadge';
import { RemediationItemStatus, RemediationRunStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { ArrowRightIcon, FileMagnifyingGlassIcon } from '@phosphor-icons/react';

import { DashboardWidget } from './DashboardWidget';

/** How many runs to show before deferring to the full list. */
const MAX_ROWS = 3;

const RunRow: FC<{ run: ActiveRemediationRunSummary; onOpen: () => void }> = observer(
  ({ run, onOpen }) => {
    const theme = useTheme();
    const { t } = useTrans();

    const settled = run.items.filter(
      (i) =>
        i.status !== RemediationItemStatus.PENDING &&
        i.status !== RemediationItemStatus.PROCESSING,
    ).length;
    const total = run.items.length;

    // Only a generating run has a meaningful percentage — once it is waiting on
    // the reviewer, progress is a human question, not a machine one.
    const showBar =
      run.status === RemediationRunStatus.PENDING ||
      run.status === RemediationRunStatus.PROCESSING;
    const needsReview = run.status === RemediationRunStatus.READY_FOR_REVIEW;

    return (
      <Box
        onClick={onOpen}
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 3,
          p: 1.75,
          cursor: 'pointer',
          '&:hover': { backgroundColor: theme.palette.grey[100] },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: showBar ? 1.25 : 0 }}>
          <RunStatusBadge status={run.status} />
          <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 500 }} noWrap>
            {run.assessmentTitle ?? t('app.remediation.list.untitledAssessment')}
          </Typography>
          {needsReview && (
            <ArrowRightIcon size={16} color={theme.palette.text.secondary} />
          )}
        </Box>

        {showBar && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <LinearProgress
              variant="determinate"
              value={total > 0 ? (settled / total) * 100 : 0}
              sx={{ flex: 1, height: 6, borderRadius: 999 }}
            />
            <Typography sx={{ fontSize: 11.5, color: 'text.tertiary', whiteSpace: 'nowrap' }}>
              {t('app.remediation.progress.doneOf', { done: settled, total })}
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);

/**
 * Dashboard surface for AI document remediation: the runs currently in flight,
 * and the ones waiting on the user to review.
 *
 * Renders nothing when there are no active runs. The dashboard is a status
 * board — an empty "no remediation" card would take a column slot to say
 * nothing, and the sidebar already offers the way in.
 *
 * This is also the only place the app proactively surfaces a run that has
 * finished generating: there is no notification channel for remediation yet
 * (see docs/03-development/remediation-frontend-gaps.md, G7), so a user who
 * navigates away is relying on this card to tell them their review is ready.
 */
export const RemediationWidget: FC = observer(() => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTrans();
  const { data } = useActiveRemediationRuns();

  const runs = data?.runs ?? [];
  if (runs.length === 0) return null;

  const awaitingReview = runs.filter(
    (r) => r.status === RemediationRunStatus.READY_FOR_REVIEW,
  ).length;

  return (
    <DashboardWidget
      icon={<FileMagnifyingGlassIcon size={20} color={theme.palette.ocean[700]} />}
      iconColor={theme.palette.ocean[100]}
      title={t('app.remediation.list.title')}
      subtitle={
        awaitingReview > 0
          ? t('app.remediation.dashboard.awaitingReview', { count: awaitingReview })
          : t('app.remediation.list.subtitle')
      }
      right={
        <Button
          variant="tertiary"
          onClick={() => navigate(routes.remediationList())}
          endIcon={<ArrowRightIcon size={16} />}
        >
          {t('app.remediation.dashboard.viewAll')}
        </Button>
      }
    >
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {runs.slice(0, MAX_ROWS).map((run) => (
          <RunRow
            key={run.runId}
            run={run}
            onOpen={() =>
              navigate(
                run.status === RemediationRunStatus.READY_FOR_REVIEW
                  ? routes.remediationReview(run.runId)
                  : routes.remediationRun(run.runId),
              )
            }
          />
        ))}
      </Box>
    </DashboardWidget>
  );
});
